import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    doc,
    getDoc,
    getFirestore,
    collection,
    addDoc,
    serverTimestamp,
} from "firebase/firestore";
import { firebaseApp } from "../../firebase";
import NavBar from "../../components/Navigation/NavBar";
import Footer from "../../components/Footer/Footer";
import { XMarkIcon } from "@heroicons/react/24/solid";
import TopAlert from "../../components/Alerts/TopAlert";
import { Helmet } from "react-helmet";
import { ArrowPathIcon } from "@heroicons/react/24/outline"; // add this import
import FormSubmissionModal from "../../components/Modal/FormSubmissionModal";


const FormPageV1 = ({ preview, parentFormData }) => {
    const { id } = useParams();
      const db = getFirestore(firebaseApp);

    const [formData, setFormData] = useState(null);
    const [alert, setAlert] = useState({ message: "", type: "info" });

    // Track answers and repeatable sections
    const [answers, setAnswers] = useState({});
    const [repeatableCounts, setRepeatableCounts] = useState({}); // { groupId: count }
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [openSubmitFormResponse, setOpenSubmitFormResponse] = useState(false);


    useEffect(() => {
        if (parentFormData) {
            setFormData(parentFormData)
            console.log(parentFormData)
        }
    }, [parentFormData]);

  if (!formData)
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-[#8BD1C7] to-[#316391]">
        <div className="flex flex-col items-center">
          <ArrowPathIcon className="h-10 w-10 text-white animate-spin" />
          <p className="mt-4 text-white text-lg font-medium">Loading form...</p>
        </div>
      </div>
    );
    // ---------------- Answer Handler ----------------
    const handleAnswerChange = (qId, value, repeatIndex = 0) => {
        const key = repeatIndex !== null ? `${qId}_${repeatIndex}` : qId;
        setAnswers((prev) => ({ ...prev, [key]: value }));
    };

    // ---------------- Repeatable Sections ----------------
    const addRepeatableSection = (groupId) => {
        setRepeatableCounts((prev) => ({
            ...prev,
            [groupId]: (prev[groupId] || 1) + 1,
        }));
    };

    const removeRepeatableSection = (groupId, index) => {
        setRepeatableCounts((prev) => {
            const newCount = prev[groupId] - 1;
            return { ...prev, [groupId]: newCount > 0 ? newCount : 1 };
        });
    };

    // ---------------- Render Questions ----------------
    const renderQuestions = (questions, groupId = null, repeatIndex = 0) =>
        questions.map((q) => {
            const key = repeatIndex !== null ? `${q.id}_${repeatIndex}` : q.id;
            const userAnswer = answers[key] || "";

            return (
                <div key={key} className="mb-4">
                    <label className="block mb-1 font-medium">
                        {q.question} {q.required && <span className="text-red-500">*</span>}
                    </label>

                    {q.type === "text" && (
                        <input
                            type="text"
                            value={userAnswer}
                            onChange={(e) => handleAnswerChange(q.id, e.target.value, repeatIndex)}
                            className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-[#316391]"
                        />
                    )}

                    {q.type === "textarea" && (
                        <textarea
                            value={userAnswer}
                            onChange={(e) => handleAnswerChange(q.id, e.target.value, repeatIndex)}
                            className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-[#316391]"
                        />
                    )}

                    {(q.type === "multiple choice" || q.type === "checkbox") &&
                        q.options.map((opt) => (
                            <label key={opt} className="flex items-center gap-2 mb-1">
                                <input
                                    type={q.type === "multiple choice" ? "radio" : "checkbox"}
                                    name={`q${q.id}_${repeatIndex}`}
                                    value={opt}
                                    checked={
                                        q.type === "multiple choice"
                                            ? userAnswer === opt
                                            : Array.isArray(userAnswer) && userAnswer.includes(opt)
                                    }
                                    onChange={(e) => {
                                        if (q.type === "multiple choice")
                                            handleAnswerChange(q.id, opt, repeatIndex);
                                        else {
                                            let newArr = Array.isArray(userAnswer) ? [...userAnswer] : [];
                                            if (e.target.checked) newArr.push(opt);
                                            else newArr = newArr.filter((a) => a !== opt);
                                            handleAnswerChange(q.id, newArr, repeatIndex);
                                        }
                                    }}
                                    className="h-4 w-4 text-[#316391] focus:ring-[#8BD1C7]"
                                />
                                <span>{opt}</span>
                            </label>
                        ))}

                    {/* Conditional branches */}
                    {q.branches &&
                        Object.entries(q.branches).map(([opt, branchQuestions]) => {
                            const showBranch =
                                q.type === "multiple choice" && userAnswer === opt;
                            if (!showBranch) return null;

                            return (
                                <div key={opt} className="ml-6 border-l border-gray-300 pl-3 mt-2">
                                    {renderQuestions(branchQuestions, groupId, repeatIndex)}
                                </div>
                            );
                        })}
                </div>
            );
        });

    // ---------------- Submit Handler ----------------
    const handleSubmit = async () => {
        const missingRequired = [];

        formData.groups?.forEach((group) => {
            const repeatCount = repeatableCounts[group.id] || 1;
            for (let ri = 0; ri < repeatCount; ri++) {
                group.questions.forEach((q) => {
                    const key = `${q.id}_${ri}`;
                    const answer = answers[key];

                    if (q.required) {
                        if (q.type === "checkbox") {
                            if (!Array.isArray(answer) || answer.length === 0) {
                                missingRequired.push(q.question);
                            }
                        } else if (!answer || answer.toString().trim() === "") {
                            missingRequired.push(q.question);
                        }
                    }

                    // Branches
                    if (q.branches && q.type === "multiple choice") {
                        const branchQuestions = q.branches[answer] || [];
                        branchQuestions.forEach((bq) => {
                            const bKey = `${bq.id}_${ri}`;
                            const bAnswer = answers[bKey];
                            if (bq.required) {
                                if (bq.type === "checkbox") {
                                    if (!Array.isArray(bAnswer) || bAnswer.length === 0) {
                                        missingRequired.push(bq.question);
                                    }
                                } else if (!bAnswer || bAnswer.toString().trim() === "") {
                                    missingRequired.push(bq.question);
                                }
                            }
                        });
                    }
                });
            }
        });

        if (missingRequired.length > 0) {
            setAlert({
                message: [
                    "Please fill in all required fields:", // heading
                    ...missingRequired, // bullets
                ].join("\n"),
                type: "error",
            });
            return;
        }

        // Submit answers
        setSubmitting(true);
        try {
            const responsesRef = collection(db, "forms", id, "responses");
            await addDoc(responsesRef, {
                answers,
                version: formData.version || serverTimestamp(),
                submittedAt: serverTimestamp(),
            });

            // setAlert({ message: "Form submitted successfully!", type: "success" });
            setAnswers({});
            setRepeatableCounts({});
            setOpenSubmitFormResponse(true);
        } catch (err) {
            console.error(err);
            setAlert({ message: "Error submitting form.", type: "error" });
        }
        setSubmitting(false);
    };

    // ---------------- Render ----------------
    return (
        <>


            {formData && (
                <Helmet>
                    <title>{formData?.title}</title>
                    <meta name="description" content={formData?.description} />
                </Helmet>
            )}

            {openSubmitFormResponse && (
                <FormSubmissionModal
                    confirmTitle="Successful Submission"
                    confirmSubtitle="Thank you for your submission. We will be staying in contact with you."
                    SGloopGroup={formData?.sgloopGroupData} // ← Use the appended group data
                    benefits={formData?.sgloopBenefits}
                    onClose={() => setOpenSubmitFormResponse(false)}
                />
            )}

            <div className="min-h-screen flex flex-col bg-gradient-to-b from-[#8BD1C7] to-[#316391]">
                <NavBar />
                <TopAlert
                    message={alert.message}
                    type={alert.type}
                    duration={4000}
                    onClose={() => setAlert({ message: "" })}
                />

                <main className="flex-grow px-4 sm:px-6 md:px-10 pt-28 sm:pt-32 pb-6">
                    <div className="max-w-[90%] mx-auto bg-white rounded-lg shadow-lg p-6">
                        {formData.bannerUrl && (
                            <div className="mb-6">
                                <img
                                    src={formData?.bannerUrl}
                                    alt="Form Banner"
                                    className="w-full max-h-64 object-contain"
                                />
                            </div>
                        )}

                        <h1 className="text-3xl font-bold mb-6">{formData.title}</h1>

                        {formData.descriptions?.map((desc, idx) => (
                            <div key={idx} className="mb-4">
                                <p className="whitespace-pre-line text-gray-600">{desc}</p>
                                {idx < formData.descriptions.length - 1 && (
                                    <div className="border-t border-gray-200 my-3"></div>
                                )}
                            </div>
                        ))}

                        {formData.groups?.map((group) => {
                            const repeatCount = repeatableCounts[group.id] || 1;
                            return (
                                <div key={group.id} className="mb-6 p-3 rounded bg-gray-50">
                                    <h2 className="text-lg font-semibold mb-3">{group.name}</h2>

                                    {group.description && (
                                        <p className="text-gray-600 mb-2 whitespace-pre-line">{group.description}</p>
                                    )}

                                    {[...Array(repeatCount)].map((_, ri) => (
                                        <div key={ri} className="mb-4 border p-2 rounded bg-white relative">
                                            {group.type === "repeatable" && repeatCount > 1 && (
                                                <button
                                                    onClick={() => removeRepeatableSection(group.id, ri)}
                                                    className="absolute top-2 right-2 text-gray-500 hover:text-red-500"
                                                    title="Remove this section"
                                                >
                                                    <XMarkIcon className="h-5 w-5" />
                                                </button>
                                            )}

                                            {renderQuestions(group.questions, group.id, ri)}
                                        </div>
                                    ))}

                                    {group.type === "repeatable" && (
                                        <button
                                            onClick={() => addRepeatableSection(group.id)}
                                            className="mt-2 px-3 py-1.5 bg-gray-200 rounded hover:bg-gray-300 text-sm flex items-center gap-1"
                                        >
                                            + Add
                                        </button>
                                    )}
                                </div>
                            );
                        })}

                        {!preview && (
                            <div className="mt-6 text-right">
                                <button
                                    onClick={handleSubmit}
                                    disabled={submitting}
                                    className="px-6 py-2 bg-[#316391] text-white rounded hover:bg-[#1f4770] disabled:opacity-50"
                                >
                                    {submitting ? "Submitting..." : "Submit"}
                                </button>
                            </div>
                        )}
                    </div>
                </main>

                <Footer />
            </div>
        </>
    );
};

export default FormPageV1;
